// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.DESKTOP.1..5 — the page side of rumo-desktop's preload
// bridge. rumo-desktop exposes `window.rumoDesktop.calls` (contextBridge);
// in a plain browser it is absent and every function here is a no-op.
//
// Bridge shape (implemented in rumo-desktop/src/preload):
//   publishState(state)            page → desktop: {ringing[], active, availability, lines[]}
//       ringing[].pass {fromUserName, note} | null — a teammate passing me a
//       call (whatsapp-calling-ownership.UI.7); lines [{inboxName}] — lines
//       I hold, for the tray's "holding the line for <inbox>".
//   onAction(cb) → unsubscribe     desktop → page: {type, callId?, availability?, muted?}
//       type ∈ answer | decline | send_to_phone | silence | toggle_mute |
//              set_muted | end | set_availability | focus_call
//   getPreferences()               → {micLabel, speakerLabel, ringLabel, noiseSuppression}
//   onPreferencesChanged(cb)       → unsubscribe
//   getNoiseSuppressor()           → {processorName, workletSource, wasmBinary} | null

export const getDesktopCallsBridge = () =>
  (typeof window !== 'undefined' && window.rumoDesktop?.calls) || null;

export const isDesktopApp = () => !!getDesktopCallsBridge();

export const buildDesktopState = ({
  ringingCalls,
  incomingPasses,
  heldLines,
  activeCall,
  heldCall,
  muted,
  onHold,
  availability,
}) => ({
  ringing: [
    ...(ringingCalls || []).map(call => ({
      id: call.id,
      contactName: call.contactName,
      inboxName: call.inboxName,
      direction: call.direction,
      pass: null,
    })),
    // whatsapp-calling-ownership.UI.7 — a passed call rings like a call, with
    // who passed it and the note.
    ...(incomingPasses || [])
      .filter(pass => pass.call)
      .map(pass => ({
        id: pass.call.id,
        contactName: pass.call.contactName,
        inboxName: pass.call.inboxName,
        direction: pass.call.direction,
        pass: {
          fromUserName: pass.fromUser?.name || '',
          note: pass.note || '',
        },
      })),
  ],
  lines: (heldLines || []).map(line => ({ inboxName: line.inboxName })),
  active: activeCall
    ? {
        id: activeCall.id,
        contactName: activeCall.contactName,
        inboxName: activeCall.inboxName,
        status: activeCall.status,
        answeredAt: activeCall.answeredAt,
        muted: !!muted,
        onHold: !!onHold,
      }
    : null,
  held: heldCall
    ? { id: heldCall.id, contactName: heldCall.contactName }
    : null,
  availability,
});

// Device ids are salted per origin, so rumo-desktop's device picker (a local
// page) stores device LABELS; they are resolved to this origin's ids here.
export const resolveDeviceId = async (
  label,
  kind,
  mediaDevices = window.navigator?.mediaDevices
) => {
  if (!label || !mediaDevices?.enumerateDevices) return null;
  try {
    const devices = await mediaDevices.enumerateDevices();
    return (
      devices.find(device => device.kind === kind && device.label === label)
        ?.deviceId || null
    );
  } catch (e) {
    return null;
  }
};

// Media preferences for KaptionCallClient. On the web the browser defaults
// apply (standard noise suppression, default devices).
export const getDesktopMediaPreferences = async () => {
  const bridge = getDesktopCallsBridge();
  if (!bridge) return { noiseSuppression: 'standard' };
  const prefs = (await bridge.getPreferences?.()) || {};
  const [micDeviceId, speakerDeviceId] = await Promise.all([
    resolveDeviceId(prefs.micLabel, 'audioinput'),
    resolveDeviceId(prefs.speakerLabel, 'audiooutput'),
  ]);
  return {
    micDeviceId,
    speakerDeviceId,
    noiseSuppression: prefs.noiseSuppression || 'standard',
    noiseSuppressorProvider: bridge.getNoiseSuppressor
      ? () => bridge.getNoiseSuppressor()
      : null,
  };
};

// DESKTOP.4 — "which devices ring": the output the ringtone plays on.
export const getDesktopRingSinkId = async () => {
  const bridge = getDesktopCallsBridge();
  if (!bridge) return null;
  const prefs = (await bridge.getPreferences?.()) || {};
  return resolveDeviceId(prefs.ringLabel, 'audiooutput');
};

// Routes a desktop action to the call store.
// whatsapp-calling-ownership.UI.7 — Answer / Decline on a passed call go to
// the pass endpoints.
const passFor = (store, callId) =>
  (store.incomingPasses?.value || []).find(pass => pass.callId === callId);

export const handleDesktopAction = (store, action = {}) => {
  const ringingId =
    action.callId ||
    store.ringingCalls.value[0]?.id ||
    store.incomingPasses?.value?.[0]?.callId;
  const pass = ringingId ? passFor(store, ringingId) : null;
  switch (action.type) {
    case 'answer':
      if (pass) return store.acceptPass(pass.id);
      return ringingId && store.accept(ringingId);
    case 'decline':
    case 'send_to_phone':
      if (pass) return store.declinePass(pass.id);
      if (action.type === 'send_to_phone')
        return ringingId && store.decline(ringingId, { forwardToPhone: true });
      return ringingId && store.decline(ringingId);
    case 'silence':
      return ringingId && store.silence(ringingId);
    case 'toggle_mute':
      return store.toggleMute();
    case 'set_muted':
      return store.setMuted(action.muted);
    case 'end':
      return store.end(action.callId || undefined);
    case 'set_availability':
      return action.availability && store.setAvailability(action.availability);
    default:
      return null;
  }
};
